import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { setGlobalLogFunction } from "../../common/helpers/apiLogger";
import { useConfigActions } from "../../contexts/ConfigContext/ConfigContext";
import JsonViewer from "../JsonViewer";
import "./DebugOverlay.scss";

interface LogEntry {
	id: number;
	timestamp: string;
	level: "log" | "error" | "warn" | "info" | "api" | "api-request" | "api-response" | "api-error";
	message: string;
	stack?: string;
	// Enhanced fields for API logging
	expanded?: boolean;
	collapsibleContent?: {
		url?: string;
		method?: string;
		headers?: Record<string, any>;
		body?: any;
		status?: number;
		responseData?: any;
	};
}

const DebugOverlay: React.FC = () => {
	const { TOGGLE_DEV_MODE } = useConfigActions();
	const [logs, setLogs] = useState<LogEntry[]>([]);
	const [isMinimized, setIsMinimized] = useState(false);
	const [opacity, setOpacity] = useState(0.5); // Direct opacity: 0=transparent, 1=opaque
	const logIdCounter = useRef(0);
	const logsEndRef = useRef<HTMLDivElement>(null);
	const lastLogCountRef = useRef(0);

	const isInteractionMode = opacity >= 0.8;

	// Capture console logs
	useEffect(() => {
		const originalConsole = {
			log: console.log,
			error: console.error,
			warn: console.warn,
			info: console.info,
		};

		const createLogEntry = (level: LogEntry["level"], args: any[]): LogEntry => {
			const message = args
				.map((arg) => (typeof arg === "object" ? JSON.stringify(arg, null, 2) : String(arg)))
				.join(" ");

			return {
				id: ++logIdCounter.current,
				timestamp: new Date().toLocaleTimeString(),
				level,
				message,
				stack: level === "error" && args[0]?.stack ? args[0].stack : undefined,
			};
		};

		const addLog = (entry: LogEntry) => {
			setLogs((prev) => [...prev, entry].slice(-100)); // Keep last 100 logs
		};

		// Enhanced addLog for API logging with direct entry
		const addEnhancedLog = (partialEntry: Partial<LogEntry>) => {
			const entry: LogEntry = {
				id: ++logIdCounter.current,
				timestamp: new Date().toLocaleTimeString(),
				expanded: false,
				...partialEntry,
			} as LogEntry;

			addLog(entry);
		};

		// Set global log function for API logger
		setGlobalLogFunction(addEnhancedLog);

		// Override console methods
		console.log = (...args) => {
			originalConsole.log(...args);
			addLog(createLogEntry("log", args));
		};
		console.error = (...args) => {
			originalConsole.error(...args);
			addLog(createLogEntry("error", args));
		};
		console.warn = (...args) => {
			originalConsole.warn(...args);
			addLog(createLogEntry("warn", args));
		};
		console.info = (...args) => {
			originalConsole.info(...args);
			addLog(createLogEntry("info", args));
		};

		// Note: API logging is now handled by apiLogger.ts with enhanced JsonViewer support
		// Remove fetch interception to avoid duplicate logging

		// Capture unhandled errors
		const handleError = (event: ErrorEvent) => {
			addLog(
				createLogEntry("error", [
					`Unhandled Error: ${event.message}`,
					`File: ${event.filename}:${event.lineno}:${event.colno}`,
					event.error?.stack,
				])
			);
		};
		const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
			addLog(
				createLogEntry("error", [
					`Unhandled Promise Rejection: ${event.reason}`,
					event.reason?.stack,
				])
			);
		};

		window.addEventListener("error", handleError);
		window.addEventListener("unhandledrejection", handleUnhandledRejection);

		// Cleanup
		return () => {
			console.log = originalConsole.log;
			console.error = originalConsole.error;
			console.warn = originalConsole.warn;
			console.info = originalConsole.info;
			// Fetch interception removed - now handled by apiLogger.ts
			window.removeEventListener("error", handleError);
			window.removeEventListener("unhandledrejection", handleUnhandledRejection);
		};
	}, []);

	// Auto scroll to bottom only when new logs are added (not when expanding/collapsing)
	useEffect(() => {
		if (!isMinimized && logs.length > lastLogCountRef.current) {
			logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
		}
		lastLogCountRef.current = logs.length;
	}, [logs, isMinimized]);

	const handleTurnOffDevMode = useCallback(() => TOGGLE_DEV_MODE(), [TOGGLE_DEV_MODE]);
	const handleToggleMinimize = useCallback(() => setIsMinimized((prev) => !prev), []);
	const handleClearLogs = useCallback(() => setLogs([]), []);
	const handleOpacityChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => setOpacity(parseFloat(event.target.value)),
		[]
	);

	const getLogClassName = (level: LogEntry["level"]) => `DebugOverlay__LogEntry--${level}`;

	const handleToggleExpanded = useCallback((logId: number) => {
		setLogs((prev) =>
			prev.map((log) => (log.id === logId ? { ...log, expanded: !log.expanded } : log))
		);
	}, []);

	return (
		<div
			className={classNames("DebugOverlay", {
				"DebugOverlay--interaction-mode": isInteractionMode,
			})}
			style={{ "--debug-opacity": opacity } as React.CSSProperties}
			draggable={false}
		>
			<div className="DebugOverlay__Header">
				<div className="DebugOverlay__Title">🐛 Debug Console ({logs.length})</div>
				<div className="DebugOverlay__Controls">
					<div className="DebugOverlay__OpacityControl">
						<span className="DebugOverlay__OpacityLabel">👁️</span>
						<input
							type="range"
							min="0"
							max="1"
							step="0.05"
							value={opacity}
							onChange={handleOpacityChange}
							className="DebugOverlay__OpacitySlider"
							title={`Opacity: ${Math.round(opacity * 100)}%`}
						/>
					</div>
					<button className="DebugOverlay__ControlBtn" onClick={handleClearLogs} title="Clear logs">
						🗑️
					</button>
					<button
						className="DebugOverlay__ControlBtn"
						onClick={handleToggleMinimize}
						title={isMinimized ? "Expand" : "Minimize"}
					>
						{isMinimized ? "⬆️" : "⬇️"}
					</button>
					<button
						className="DebugOverlay__ControlBtn DebugOverlay__ControlBtn--danger"
						onClick={handleTurnOffDevMode}
						title="Turn off Dev Mode"
					>
						❌
					</button>
				</div>
			</div>

			{!isMinimized && (
				<div className="DebugOverlay__Content">
					<div className="DebugOverlay__Info">
						<div className="DebugOverlay__InfoItem">
							<strong>Dev Mode:</strong> ON
						</div>
						<div className="DebugOverlay__InfoItem">
							<strong>Env:</strong> {process.env.NODE_ENV}
						</div>
						<div className="DebugOverlay__InfoItem">
							<strong>URL:</strong> {window.location.pathname}
						</div>
					</div>

					<div className="DebugOverlay__Logs">
						{logs.length === 0 ? (
							<div className="DebugOverlay__NoLogs">No logs yet...</div>
						) : (
							logs.map((log) => (
								<div
									key={log.id}
									className={classNames("DebugOverlay__LogEntry", getLogClassName(log.level), {
										"DebugOverlay__LogEntry--expandable": log.collapsibleContent,
										"DebugOverlay__LogEntry--expanded": log.expanded,
									})}
								>
									<div
										className="DebugOverlay__LogHeader"
										onClick={() => log.collapsibleContent && handleToggleExpanded(log.id)}
										style={{ cursor: log.collapsibleContent ? "pointer" : "default" }}
									>
										<div className="DebugOverlay__LogHeader-Left">
											<span className="DebugOverlay__LogTime">{log.timestamp}</span>
											<span className="DebugOverlay__LogLevel">{log.level.toUpperCase()}</span>
										</div>
										{log.collapsibleContent && (
											<span className="DebugOverlay__LogToggle">{log.expanded ? "🔽" : "▶️"}</span>
										)}
									</div>
									<div className="DebugOverlay__LogMessage">{log.message}</div>
									{log.stack && <div className="DebugOverlay__LogStack">{log.stack}</div>}
									{log.expanded && log.collapsibleContent && (
										<div className="DebugOverlay__LogCollapsible">
											{log.collapsibleContent.url && (
												<div className="DebugOverlay__LogDetail">
													<strong>URL:</strong> {log.collapsibleContent.url}
												</div>
											)}
											{log.collapsibleContent.method && (
												<div className="DebugOverlay__LogDetail">
													<strong>Method:</strong> {log.collapsibleContent.method}
												</div>
											)}
											{log.collapsibleContent.status && (
												<div className="DebugOverlay__LogDetail">
													<strong>Status:</strong> {log.collapsibleContent.status}
												</div>
											)}
											{log.collapsibleContent.headers && (
												<div className="DebugOverlay__LogDetail">
													<strong>Headers:</strong>
													<div className="DebugOverlay__JsonViewer">
														<JsonViewer data={log.collapsibleContent.headers} collapsed={true} />
													</div>
												</div>
											)}
											{log.collapsibleContent.body && (
												<div className="DebugOverlay__LogDetail">
													<strong>Body:</strong>
													<div className="DebugOverlay__JsonViewer">
														<JsonViewer data={log.collapsibleContent.body} collapsed={true} />
													</div>
												</div>
											)}
											{log.collapsibleContent.responseData && (
												<div className="DebugOverlay__LogDetail">
													<strong>Response:</strong>
													<div className="DebugOverlay__JsonViewer">
														<JsonViewer
															data={log.collapsibleContent.responseData}
															collapsed={true}
														/>
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							))
						)}
						<div ref={logsEndRef} />
					</div>
				</div>
			)}
		</div>
	);
};

export default DebugOverlay;
